/**
 * Flota — karty drogowe (kolejka, tablica, szczegół, panel decyzji): `fleet.web.lb.*`, doklejone w `extra.ts`.
 * `fleetLogbooksUiEn` jest typowany względem `fleetLogbooksUiPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetLogbooksUiPl = {
  lb: {
    plan: 'plan {km}',
    chipShown: 'Kart na liście',
    filters: 'Filtry',
    emptyFiltered: 'Żadna karta nie pasuje do filtrów.',
    col: { driverRoute: 'Kierowca / trasa' },
    view: { board: 'Tablica' },
    boardColumnEmpty: 'Brak kart',
    boardMore: 'oraz {count} starszych — zobacz w tabeli',
    flagsBanner: 'Wymaga uwagi',
    tile: {
      aria: 'Szybkie filtry kart według statusu',
      queue: 'Do weryfikacji',
      queueHint: 'wysłane przez kierowców',
      flagged: 'Z flagami',
      flaggedHint: 'do weryfikacji, wymagają oceny',
      discrepancy: 'Niezgodności',
      discrepancyHint: 'oznaczone jako niezgodne',
      returned: 'Zwrócone',
      returnedHint: 'czekają na poprawę kierowcy',
      open: 'W trakcie',
      openHint: 'trasy właśnie jadą',
      openMineHint: 'trwa Twoja trasa',
      submittedMine: 'Wysłane',
      submittedMineHint: 'czekają na weryfikację',
      returnedMine: 'Do poprawy',
      returnedMineHint: 'zwrócone przez weryfikującego',
      approved: 'Zatwierdzone',
      approvedHint: 'zamknięte i rozliczone',
    },
    bulk: {
      open: 'Zatwierdź czyste ({count})',
      title: 'Zatwierdź czyste karty',
      description: 'Zatwierdzimy karty bez flag, z policzonym przebiegiem — po jednej, w kolejności z listy.',
      willApprove: 'do zatwierdzenia',
      skippedOwn: 'Pomijamy własne karty ({count}) — nie możesz zatwierdzać własnej karty.',
      skippedFlagged: 'Pomijamy karty z flagami lub bez przebiegu ({count}) — wymagają ręcznej oceny.',
      scopeNote:
        'Dotyczy kart widocznych na bieżącej stronie listy. Każda decyzja trafia do historii karty i audytu.',
      confirm: 'Zatwierdź ({count})',
      approved: 'Zatwierdzono kart: {count}',
      failed: 'Nie udało się zatwierdzić: {count}',
      unknownError: 'nieoczekiwany błąd',
    },
    panel: {
      title: 'Decyzja recenzenta',
      prev: 'Poprzednia karta w kolejce',
      next: 'Następna karta w kolejce',
      position: '{n} z {total}',
      queueSize: 'w kolejce: {total}',
      commentHint: 'wymagany przy zwrocie i niezgodności',
      shortcuts: 'Skróty: A zatwierdź · R zwróć · D niezgodność · N następna · P poprzednia karta w kolejce.',
      ownCard: 'To Twoja karta — nie możesz jej zweryfikować. Zrobi to inny uprawniony użytkownik.',
      noActions: {
        forbidden: 'Nie masz uprawnienia do weryfikacji kart.',
        approved: 'Karta jest zatwierdzona — brak dalszych decyzji.',
        notSubmitted: 'Karta nie czeka na weryfikację.',
      },
    },
    odo: {
      title: 'Licznik i plan',
      description: 'Odczyty z początku i końca trasy oraz wykonanie względem planu.',
      start: 'Start',
      end: 'Koniec',
      barAria: 'Przebieg {actual} km, plan {planned} km',
      planMarker: 'znacznik: plan {km} km',
      noPlan: 'brak planu',
      vsPlan: 'względem planu',
      noData: 'Brak przebiegu i planu — karta nie została jeszcze zamknięta.',
      meterNote:
        'W trakcie trasy wymieniono lub skorygowano licznik — przebieg policzono z odczytów, więc może się różnić od prostej różnicy stanów.',
    },
    related: {
      title: 'Powiązane wpisy',
      description: 'To, co kierowca zapisał przy tej trasie.',
      noChecklists: 'Brak checklist powiązanych z tą kartą.',
      defect: 'usterka',
      warning: 'uwagi',
      allOk: 'bez uwag',
      noFuel: 'Brak tankowań przypisanych do karty.',
      noExpenses: 'Brak kosztów przypisanych do karty.',
      noIncidents: 'Brak zgłoszeń przypisanych do karty.',
    },
    route: {
      title: 'Plan a wykonanie trasy',
      description: 'Trasa {id}',
      open: 'Otwórz trasę',
      notFound: 'Karta wskazuje trasę zewnętrzną, ale nie znaleziono jej w pobranym oknie dat.',
      planned: 'Plan',
      actual: 'Wykonanie',
      stops: 'Przystanki',
      barAria: 'Plan {planned}, wykonanie {actual}',
      tolerance: 'tolerancja {pct}%',
      manyLogbooks: 'Do trasy przypisano kart: {count}',
    },
    hero: {
      duration: 'Czas trasy',
      since: 'od {time}',
      speed: 'Średnia prędkość',
      speedHint: 'orientacyjnie, z czasu karty',
      from: 'start {km}',
    },
  },
};

export const fleetLogbooksUiEn: Shape<typeof fleetLogbooksUiPl> = {
  lb: {
    plan: 'plan {km}',
    chipShown: 'Logbooks listed',
    filters: 'Filters',
    emptyFiltered: 'No logbook matches the filters.',
    col: { driverRoute: 'Driver / route' },
    view: { board: 'Board' },
    boardColumnEmpty: 'No logbooks',
    boardMore: 'and {count} older — see the table',
    flagsBanner: 'Needs attention',
    tile: {
      aria: 'Quick logbook filters by status',
      queue: 'To review',
      queueHint: 'submitted by drivers',
      flagged: 'Flagged',
      flaggedHint: 'to review, need a closer look',
      discrepancy: 'Discrepancies',
      discrepancyHint: 'marked as inconsistent',
      returned: 'Returned',
      returnedHint: 'waiting for the driver to fix',
      open: 'In progress',
      openHint: 'trips on the road now',
      openMineHint: 'your trip is in progress',
      submittedMine: 'Submitted',
      submittedMineHint: 'waiting for review',
      returnedMine: 'To fix',
      returnedMineHint: 'returned by the reviewer',
      approved: 'Approved',
      approvedHint: 'closed and settled',
    },
    bulk: {
      open: 'Approve clean ({count})',
      title: 'Approve clean logbooks',
      description:
        'We will approve logbooks without flags and with a calculated distance — one by one, in list order.',
      willApprove: 'to approve',
      skippedOwn: 'Skipping your own logbooks ({count}) — you cannot approve your own logbook.',
      skippedFlagged:
        'Skipping flagged logbooks or those without distance ({count}) — they need manual review.',
      scopeNote:
        'Applies to logbooks visible on the current list page. Every decision is recorded in the logbook history and audit trail.',
      confirm: 'Approve ({count})',
      approved: 'Approved logbooks: {count}',
      failed: 'Could not approve: {count}',
      unknownError: 'unexpected error',
    },
    panel: {
      title: 'Reviewer decision',
      prev: 'Previous logbook in the queue',
      next: 'Next logbook in the queue',
      position: '{n} of {total}',
      queueSize: 'in queue: {total}',
      commentHint: 'required for return and discrepancy',
      shortcuts:
        'Shortcuts: A approve · R return · D discrepancy · N next · P previous logbook in the queue.',
      ownCard: 'This is your own logbook — you cannot review it. Another authorised user will.',
      noActions: {
        forbidden: 'You are not allowed to review logbooks.',
        approved: 'The logbook is approved — no further decisions.',
        notSubmitted: 'The logbook is not waiting for review.',
      },
    },
    odo: {
      title: 'Odometer and plan',
      description: 'Readings at the start and end of the trip and the result against the plan.',
      start: 'Start',
      end: 'End',
      barAria: 'Distance {actual} km, plan {planned} km',
      planMarker: 'marker: plan {km} km',
      noPlan: 'no plan',
      vsPlan: 'vs plan',
      noData: 'No distance and no plan — the logbook is not closed yet.',
      meterNote:
        'The odometer was replaced or corrected during the trip — distance is calculated from readings, so it may differ from a simple difference.',
    },
    related: {
      title: 'Related entries',
      description: 'What the driver recorded during this trip.',
      noChecklists: 'No checklists linked to this logbook.',
      defect: 'defect',
      warning: 'remarks',
      allOk: 'no remarks',
      noFuel: 'No fuel entries assigned to this logbook.',
      noExpenses: 'No expenses assigned to this logbook.',
      noIncidents: 'No incident reports assigned to this logbook.',
    },
    route: {
      title: 'Route plan vs actual',
      description: 'Route {id}',
      open: 'Open route',
      notFound: 'The logbook points to an external route, but it was not found in the fetched date window.',
      planned: 'Plan',
      actual: 'Actual',
      stops: 'Stops',
      barAria: 'Plan {planned}, actual {actual}',
      tolerance: 'tolerance {pct}%',
      manyLogbooks: 'Logbooks assigned to the route: {count}',
    },
    hero: {
      duration: 'Trip time',
      since: 'since {time}',
      speed: 'Average speed',
      speedHint: 'indicative, from logbook times',
      from: 'start {km}',
    },
  },
};
